import Sticky from "../../../../common/components/sticky";
import { trackEvent } from "@kunduz/utility-analytics";
import { useEffect } from "react";
import { LearningStartCourse } from "../../components/learningStartCourse/learningStartCourse";
import { ILearning } from "@kunduz/data-access-learning-api";
import pageStyles from "./modulePage.module.scss";
import { NextSeo } from "next-seo";
import { useAppContext } from "@kunduz/feature-website-context";
import { useTranslation } from "next-i18next";
import { LearningSimiliarCourses } from "../../components/learningSimilarCourses/learningSimilarCourses";
import QuestionSideCtaLoader, {
  SideCTAModes,
} from "../../../../common/components/sideCta";
import { LearningBreadcrumb } from "../../components/learningBreadcrumb/learningBreadcrumb";
import Link from "next/link";
import { LearningConceptItem } from "../../components/conceptItem/learningConceptItem";
import { getContentUrl, ReservedWords } from "@kunduz/utility-common";
import {
  WebsiteTrFooter,
  BlogQIMobileCta,
  HeaderComponent,
} from "@kunduz/feature-landing-pages";
import { type IContent } from "@kunduz/data-access-cms-api";

const ModulePage = (props: ModulePageProps) => {
  const { student } = useAppContext();
  const { t } = useTranslation("kunduzcom_learning");

  useEffect(() => {
    const payload = {};

    props.pageMeta.breadcrumb?.forEach((element, index) => {
      payload[`module_${props.pageMeta.breadcrumb?.length - index}`] =
        element.name;
    });

    trackEvent("Student Module Page Viewed", payload);
  }, []);

  return (
    <>
      <NextSeo
        title={props.pageMeta?.seo_meta?.meta_title}
        description={props.pageMeta?.seo_meta?.meta_desc}
        canonical={getContentUrl({
          domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
          lang: props.lang,
          contentType: ReservedWords.TR_CONTENT_MODULE,
          segments: [props.moduleSlug],
        })}
        robotsProps={{
          maxImagePreview: "large",
        }}
      />

      <HeaderComponent
        language={props.lang}
        headerContactShow={false}
        headerBannerShow={!!props.headerBanner}
        headerBannerData={props.headerBanner}
        headerShow={!!props.headerItems}
        headerItems={props.headerItems}
      />

      <BlogQIMobileCta />

      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.breadcrumb}>
          <LearningBreadcrumb
            items={props.pageMeta.breadcrumb}
            homePageLabel={t("learning_breadcrumb_homepage_label")}
            lang={props.lang}
            linkAs={Link}
          />
        </div>
        <main className={pageStyles.content}>
          <div className={pageStyles.headersContainer}>
            <h1>{props.pageMeta.name}</h1>
            <div className={pageStyles.subTitle}>
              {props.pageMeta.seo_meta.sub_title}
            </div>
            {props.pageMeta.seo_meta?.short_desc && (
              <div className={pageStyles.shortDesc}>
                {props.pageMeta.seo_meta.short_desc}
              </div>
            )}
          </div>
          {props.pageMeta.concepts?.map((concept, index) => (
            <LearningConceptItem
              key={concept.id}
              index={index}
              isMobile={props.isMobile}
              materials={concept.materials}
              name={concept.name}
              // the reason for using component_desc here: the content team entered the short_desc and component_desc incorrectly. it will be changed later.
              shortDesc={concept.seo_meta.component_desc}
              moduleSlug={props.moduleSlug}
              conceptSlug={concept.slug}
              viewAllText={t(
                "learning_module_page_concept_item_view_all_cta_label",
              )}
              lang={props.lang}
              linkAs={Link}
            />
          ))}
          <LearningSimiliarCourses
            isMobile={props.isMobile}
            title={t("learning_similar_contents_header_modules")}
            goPathLabel={t("learning_learning_path_item_view_unit")}
            numberOfSubjectsLabel={t("learning_number_of_concepts_icon_label")}
            numberOfVideosLabel={t(
              "learning_number_of_video_components_icon_label",
            )}
            id={props.pageMeta.id}
            type="modules"
            lang={props.lang}
            linkAs={Link}
          />
          {props.pageMeta.seo_meta?.long_desc && (
            <div className={pageStyles.longDescription}>
              {props.pageMeta.seo_meta?.long_desc}
            </div>
          )}
        </main>
        <div className={pageStyles.sticky}>
          <Sticky top={100}>
            {student.data ? (
              <LearningStartCourse
                type="module"
                id={props.pageMeta.id}
                canUserStartCourse={true}
                description={props.pageMeta.seo_meta?.component_desc}
                title={props.pageMeta.seo_meta?.component_title}
                lang={props.lang}
              />
            ) : (
              <QuestionSideCtaLoader
                isMobile={props.isMobile}
                lang={props.lang}
                mode={SideCTAModes.LEARNING}
                translationFile="kunduzcom_learning"
              />
            )}
          </Sticky>
        </div>
      </div>

      <WebsiteTrFooter />
    </>
  );
};

export interface ModulePageProps {
  isMobile: boolean;
  isAuth: boolean;
  lang: "tr" | "en";
  pageMeta: ILearning.ModuleResponse;
  moduleSlug: string;
  headerItems: IContent.HeaderItemsResponse;
  headerBanner?: IContent.HeaderBannerResponse;
}

export default ModulePage;
